import cx from 'classnames'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Styles from './InstagramReelPreview.module.scss'
import { InstagramIntegration } from 'api/clients/ExternalIntegrationsClient'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { useTruncateText } from '../hooks/useTruncateText'
import { MediaObject } from 'types'
import { ReactNode } from 'react'
import { ReelsVideoPlayer } from '../components/ReelsVideoPlayer/ReelsVideoPlayer'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditInDesignerOverlay } from '../components/EditInDesignerOverlay/EditInDesignerOverlay'
import { isVideo } from 'components/ImageMenu/utils'
import { DesignChannel } from 'components/NewFileModal/Types'
import { Footer } from './Footer'
import { Actions } from './Actions'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'

interface InstagramReelPreviewProps {
  integration?: InstagramIntegration
  mediaObjects?: MediaObject[]
  content?: string
  post?: ScheduledPostShow
  showVideoControls?: boolean
  onEdit?: () => void
  containerClassName?: string
  customPreviewContent?: ReactNode
  isActive?: boolean
  isTransitioning?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  isGenerating?: boolean
  onClose?: () => void
  alwaysTruncateCaption?: boolean
}

interface MoreProps {
  isTruncated: boolean
  setIsTruncated: React.Dispatch<React.SetStateAction<boolean>>
}

export const InstagramReelPreview = ({
  integration,
  mediaObjects,
  content,
  showVideoControls,
  onEdit,
  containerClassName,
  customPreviewContent,
  isActive = true,
  isGenerating = false,
  isTransitioning = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  onClose,
  alwaysTruncateCaption = false,
  post,
}: InstagramReelPreviewProps) => {
  const { name, username, avatar } = integration
    ? extractProfileInfo(integration)
    : { name: 'User', username: 'user', avatar: Silhouette }

  const highlightClassNames = {
    mentionClassName: Styles.highlight,
    hashtagClassName: Styles.highlight,
  }

  const { isTruncated, setIsTruncated, shouldShowMoreButton, textRef } = useTruncateText({
    content: content || '',
    truncatedByDefault: alwaysTruncateCaption,
  })

  const showMoreButton = content && shouldShowMoreButton
  const video = mediaObjects?.length && isVideo({ url: mediaObjects[0].source_url, type: mediaObjects[0].mime_type })

  return (
    <ConditionalWrapper
      condition={showHoverActionOverlay && almanacFileKey}
      wrapper={(children) => (
        <EditInDesignerOverlay
          channel={DesignChannel.InstagramReel}
          almanacFileKey={almanacFileKey as string}
          borderRadius={0}
          position={video ? 'top' : undefined}
          onClose={onClose}
          className={isGenerating ? Styles.disableHover : undefined}
          caption={content}
        >
          {children}
        </EditInDesignerOverlay>
      )}
    >
      <div className={cx(Styles.container, containerClassName)}>
        <div className={Styles.content}>
          {customPreviewContent || (
            <ReelsVideoPlayer
              mediaObjects={mediaObjects}
              showVideoControls={showVideoControls}
              isActive={isActive}
              className={Styles.preview}
              dataTestId="instagram-video"
              username={username}
              almanacFileKey={almanacFileKey as string}
              isGenerating={isGenerating}
            />
          )}
        </div>

        <Footer
          integration={integration}
          content={content}
          onEdit={onEdit}
          isGenerating={isGenerating}
          alwaysTruncateCaption={alwaysTruncateCaption}
          post={post}
        />
        <Actions />
      </div>
    </ConditionalWrapper>
  )
}
